import { DateTime } from '@mode-switch/express';
import { fromChartSelectors } from '@mode/charts/domain';
import { AccountResource } from '@mode/legacy/data-access-model-js';
import { FLAMINGO_RESERVED_REFS, makePagingResult, shareReplayDefault } from '@mode/shared/util-js';
import { Switch } from '@mode/shared/util-switch';
import { fromDatasetResultSelectors, fromFlamingoSelectors, fromSwitchViewSelectors } from '@mode/switch-views/domain';
import { combineLatest as staticCombineLatest } from 'rxjs';
import { combineLatest, filter, map, switchMap } from 'rxjs/operators';
import { reportContextSelector } from '~/reducers/selectors';
import { ChartDesignerSelectors } from '~/selectors/designer.selectors';
import { selectedReportDisplayNameSelector, selectedReportLinks } from '../reducers/selectors';
import { ChartSelectors } from '../selectors/chart.selectors';
import { ExplorerSelectors } from '../selectors/explorer.selectors';
import { SelectedReportSelectors } from '../selectors/selected-report.selectors';
import { AppSelect } from '../types/main.types';
import { VizViewerObservables } from '../types/viz-designer.types';

/**
 * Make the observables object for `viz-viewer`.
 */
export function makeVizViewerObservables(store: AppSelect): VizViewerObservables {
  const token$ = store.select(ExplorerSelectors.getToken);
  const queryToken$ = token$.pipe(
    switchMap((token) => store.select(fromChartSelectors.createQueryTokenSelector(token)))
  );
  const queryRunToken$ = queryToken$.pipe(
    switchMap((queryToken) => store.selectNoEmpty(SelectedReportSelectors.createQueryRunTokenSelector(queryToken)))
  );
  const switchViewToken$ = token$.pipe(
    switchMap((token) => store.selectNoEmpty(fromChartSelectors.createSwitchViewTokenSelector(token)))
  );
  const runState$ = queryToken$.pipe(
    switchMap((queryToken) => store.selectNoEmpty(fromDatasetResultSelectors.getQueryRunStateByQueryToken(queryToken)))
  );
  const explorerPlanId$ = store.selectNoEmpty(
    fromFlamingoSelectors.createPlanIdSelector(FLAMINGO_RESERVED_REFS.ChartExplorer)
  );
  const binSizePlanId$ = store.selectNoEmpty(
    fromFlamingoSelectors.createPlanIdSelector(
      `${FLAMINGO_RESERVED_REFS.BinSize}#${FLAMINGO_RESERVED_REFS.ChartExplorer}`
    )
  );

  const isPurged$ = queryToken$.pipe(
    switchMap((queryToken) => store.selectNoEmpty(fromDatasetResultSelectors.getDatasetResultByQueryToken(queryToken))),
    map((datasetResult) => datasetResult.isPurged)
  );

  const isValid$ = store.select(ExplorerSelectors.getIsValid).pipe(shareReplayDefault());
  const isComplete$ = store.select(ExplorerSelectors.getIsComplete).pipe(shareReplayDefault());
  const canRender$ = isValid$.pipe(
    combineLatest(isComplete$),
    map(([isValid, isComplete]) => isValid && isComplete)
  );
  const view$ = queryToken$.pipe(
    combineLatest(store.select(fromSwitchViewSelectors.getModel)),
    map(([queryToken, model]) => Switch.Views.find(model, queryToken)),
    shareReplayDefault()
  );
  const title$ = store.select(ExplorerSelectors.getSelectedChartTitle);

  const planId$ = token$.pipe(
    switchMap(
      () => store.select(fromFlamingoSelectors.createPlanIdSelector(FLAMINGO_RESERVED_REFS.ChartExplorer)),
      (token, planId) => ({ token, planId })
    )
  );

  const planError$ = planId$.pipe(
    switchMap(({ planId }) => store.select(fromFlamingoSelectors.createErrorsSelector(planId))),
    shareReplayDefault()
  );

  const paging$ = planId$.pipe(
    switchMap(({ planId, token }) =>
      staticCombineLatest([
        store.select(ChartDesignerSelectors.createPagingSelector(token)),
        store.selectNoEmpty(fromFlamingoSelectors.createCountSelector(planId)),
      ]).pipe(map(([paging, count]) => makePagingResult(paging, count)))
    ),
    filter((p) => p != null)
  );

  const chartState$ = store.selectNoEmpty(ExplorerSelectors.getChart).pipe(
    filter((c) => c.resource != null) // Non-charts will not have their state filled out
  );

  const model$ = store.select(fromSwitchViewSelectors.getModel);

  const reportDisplayName$ = store.select(selectedReportDisplayNameSelector);

  // Get meta data related to viz
  const vizResource$ = store.selectNoEmpty(
    fromChartSelectors.createChartResourceSelector(FLAMINGO_RESERVED_REFS.ChartExplorer)
  );
  const vizWebLink$ = vizResource$.pipe(
    map((chartResource) => `${window.location.origin}${chartResource.links('report_viz_web')['href']}`)
  );
  const links$ = store.select(selectedReportLinks);
  const vizCreator$ = vizResource$.pipe(
    switchMap((chartResource) => chartResource.links('creator').$follow()),
    map((creator: AccountResource) => creator.name)
  );
  const vizState$ = store.selectNoEmpty(ExplorerSelectors.getChart);
  const canUndo$ = vizState$.pipe(map((vizState) => vizState.canUndo));
  const canRedo$ = vizState$.pipe(map((vizState) => vizState.canRedo));
  const hasSubTitle$ = title$.pipe(
    combineLatest(vizCreator$),
    map(([hasTitle, hasCreator]) => !!(hasTitle && hasCreator))
  );

  const isAutoUpdateDisabled$ = store.select(ExplorerSelectors.getSelectedChartIsAutoUpdateDisabled);
  const manuallyApplyChanges$ = store.select(ExplorerSelectors.getSelectedChartManuallyApplyChanges);
  const isAutoOrManualRender$ = staticCombineLatest([isAutoUpdateDisabled$, manuallyApplyChanges$]).pipe(
    map(([isAutoUpdateDisabled, manuallyApplyChanges]) => !isAutoUpdateDisabled || manuallyApplyChanges)
  );
  // The unappliedChangesCount may be negative if the changes are a result of the user undoing
  // previously applied changes. This observable maps to the absolute value so that the consumers
  // just see the raw number of changes.
  const unappliedChangesCount$ = store
    .select(ExplorerSelectors.getSelectedChartUnappliedChanges)
    .pipe(map((unappliedChanges) => Math.abs(unappliedChanges)));

  const binSizePlanLoading$ = binSizePlanId$.pipe(
    switchMap((planId) => store.select(fromFlamingoSelectors.createLoadingSelector(planId)))
  );

  const defaultYearStart$ = store
    .select(reportContextSelector)
    .pipe(
      map((reportContext) => DateTime.yearStart(reportContext.settings?.['year_start']) ?? DateTime.YearStart.January)
    );

  const defaultWeekStart$ = store
    .select(reportContextSelector)
    .pipe(
      map((reportContext) => DateTime.weekStart(reportContext.settings?.['week_start']) ?? DateTime.WeekStart.Sunday)
    );

  return {
    token$,
    planId$,
    chartType$: store.select(ExplorerSelectors.getSelectedChartType),
    queryToken$,
    queryRunToken$,
    switchViewToken$,
    encoding$: store.select(ExplorerSelectors.getSelectedChartEncoding),
    format$: store.select(ExplorerSelectors.getSelectedChartFormat),
    colorPaletteToken$: store.select(ExplorerSelectors.getSelectedChartPaletteToken),
    fieldFormats$: store.select(ExplorerSelectors.getSelectedChartFieldFormatMap),
    fieldColors$: store.select(ExplorerSelectors.getSelectedChartFieldColorMap),
    facetSizes$: store.select(ExplorerSelectors.getSelectedChartFacetSizeMap),
    isAutoUpdateDisabled$,
    manuallyApplyChanges$,
    isAutoOrManualRender$,
    unappliedChangesCount$: unappliedChangesCount$,
    title$: title$,
    caption$: store.select(ExplorerSelectors.getSelectedChartCaption),
    isValid$,
    isComplete$,
    canRender$,
    runFailed$: runState$.pipe(
      map((state) => state === 'failed'),
      shareReplayDefault()
    ),
    view$,
    chartState$,
    model$,
    planError$,
    planLoading$: explorerPlanId$.pipe(
      switchMap((planId) => store.select(fromFlamingoSelectors.createLoadingSelector(planId))),
      combineLatest(canRender$),
      map(([loading, canRender]) => loading && canRender)
    ),
    planPageLoading$: explorerPlanId$.pipe(
      switchMap((planId) => store.select(fromFlamingoSelectors.createPageLoadingSelector(planId))),
      combineLatest(canRender$),
      map(([loading, canRender]) => loading && canRender)
    ),
    planFailed$: explorerPlanId$.pipe(
      switchMap((planId) => store.select(fromFlamingoSelectors.createFailedSelector(planId)))
    ),
    planSucceeded$: explorerPlanId$.pipe(
      switchMap((planId) => store.select(fromFlamingoSelectors.createReadySelector(planId)))
    ),

    renderModel$: planId$.pipe(
      switchMap(({ token, planId }) => store.select(ChartSelectors.createRenderDataModelSelector(token, planId)))
    ),
    columns$: store.select(fromFlamingoSelectors.createColumnsSelector(FLAMINGO_RESERVED_REFS.ChartExplorer)),
    paging$,

    reportDisplayName$,
    vizWebLink$,
    vizCreator$,
    canUndo$,
    canRedo$,
    hasSubTitle$,
    links$,
    binSizePlanLoading$,

    defaultYearStart$,
    defaultWeekStart$,
    isPurged$,
  };
}
